import { api } from './api';
import type { DeleteResponse, PaginatedResponse } from '../types';

// API Response wrapper type
interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

// -----------------------------------------------------------------------------
// Types (mirrors backend user_master_schema.py)
// -----------------------------------------------------------------------------

export interface Contact {
  email: string;
  phoneNo: string;
}

/** A linked OAuth/SSO account. email always mirrors the account's contact.email. */
export interface ExternalLogin {
  provider: 'google' | 'microsoft';
  subject: string;
  email: string;
  tenantId?: string | null;
  linkedAt?: string | null;
  lastLoginAt?: string | null;
}

export interface User {
  _id: string;
  personnelMasterId?: string | null;
  appId: string;
  contact?: Contact | null;
  contactSource?: 'personnel' | 'manual';
  userId?: string;
  ministryId?: string;
  fcmToken?: string;
  mpin?: string;
  externalLogins?: ExternalLogin[] | null;
  defaultlanguageId?: number;
  isRegistered: boolean;
  isVerified: boolean;
  isLoggedIn: boolean;
  userStatus?: string;
  blocked: boolean;
  reasonForBlock?: string;
  lastLoginDateTime?: string;
  failedLoginAttemptCounter: number;
  // Audit fields
  isDelete: boolean;
  createdBy?: string;
  createdAt?: string;
  createdIp?: string;
  updatedBy?: string;
  updatedAt?: string;
  updatedIp?: string;
  deletedBy?: string;
  deletedAt?: string;
}

export interface UserCreateRequest {
  personnelMasterId?: string | null;
  appId: string;
  userId: string;
  contact?: Contact;
  mpin?: string;
  password?: string;
  fcmToken?: string;
  defaultlanguageId?: number;
}

export interface UserUpdateRequest {
  personnelMasterId?: string | null;
  contact?: Contact;
  appId?: string;
  // mpin & password are NOT edited here — they have dedicated set/reset flows
  // (and forgot-password / forgot-mpin flows are planned).
  fcmToken?: string;
  defaultlanguageId?: number;
  isRegistered?: boolean;
  isVerified?: boolean;
  isLoggedIn?: boolean;
  userStatus?: string;
  blocked?: boolean;
  reasonForBlock?: string;
}

/** What a user delete will cascade-archive; shown for approval before deleting. */
export interface UserDependencies {
  canDelete: boolean;
  cascade: boolean;
  total: number;
  dependents: {
    roleAssignments: Array<{ _id: string; role: string; unit: string }>;
    devices: Array<{ _id: string; deviceId?: string; deviceName?: string }>;
    sessions: number;
  };
}

export interface UserQueryParams {
  personnelId?: string;
  appId?: string;
  app_id?: string;
  include_deleted?: boolean;
  include_global?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  filters?: string;
}

/** One project an account exists in, within a grouped (per-person) user row. */
export interface GroupedUserProject {
  _id: string;
  appId: string;
  userId?: string;
  isRegistered: boolean;
  isVerified: boolean;
  blocked: boolean;
  userStatus?: string | null;
  createdAt?: string;
}

/** A user combined across projects — one row per person, carrying their projects. */
export interface GroupedUser {
  key: string;
  userId?: string;
  personnelMasterId?: string | null;
  email?: string | null;
  phoneNo?: string | null;
  contact?: Contact | null;
  contactSource?: 'personnel' | 'manual';
  defaultlanguageId?: number | null;
  projectCount: number;
  registeredCount: number;
  blockedCount: number;
  projects: GroupedUserProject[];
}

/** One project entry in a grouped save: keep+update (_id), add (no _id), or remove. */
export interface UserGroupProjectInput {
  appId: string;
  _id?: string;
  remove?: boolean;
  userStatus?: string | null;
  blocked?: boolean;
}

/** Reconcile a person across projects in one save. */
export interface UserGroupUpdatePayload {
  personnelMasterId?: string | null;
  contact?: Contact;
  defaultlanguageId?: number;
  projects: UserGroupProjectInput[];
}

export interface UserGroupUpdateResult {
  updated: string[];
  created: string[];
  deleted: string[];
  failed: { appId: string; reason: string }[];
}

export const usersService = {
  // List users (paginated)
  getAll: async (params?: UserQueryParams): Promise<PaginatedResponse<User>> => {
    const response = await api.get<PaginatedResponse<User>>('/api/v1/users/list', { params });
    return response.data;
  },

  // List users COMBINED per person — one row per user carrying their project list.
  getGrouped: async (params?: UserQueryParams): Promise<PaginatedResponse<GroupedUser>> => {
    const response = await api.get<PaginatedResponse<GroupedUser>>('/api/v1/users/list-grouped', { params });
    return response.data;
  },

  // A person's whole group (shared identity + all project accounts) from any account id.
  getGroupDetail: async (userId: string): Promise<GroupedUser> => {
    const response = await api.get<ApiResponse<GroupedUser>>(`/api/v1/users/grouped/${userId}`);
    return response.data.data;
  },

  // Reconcile a person across projects in one save (shared fields + add/update/remove).
  updateGroup: async (userId: string, payload: UserGroupUpdatePayload): Promise<UserGroupUpdateResult> => {
    const response = await api.put<ApiResponse<UserGroupUpdateResult>>(`/api/v1/users/grouped/${userId}`, payload);
    return response.data.data;
  },

  // Get user by ID
  getById: async (id: string): Promise<User> => {
    const response = await api.get<ApiResponse<User>>(`/api/v1/users/get/${id}`);
    return response.data.data;
  },

  // Create a new user (link personnel to app)
  create: async (payload: UserCreateRequest): Promise<User> => {
    const response = await api.post<ApiResponse<User>>('/api/v1/users/create', payload);
    return response.data.data;
  },

  // Create ONE user across several projects in a single call — the backend makes
  // one user_master row per project (best effort: failures are reported per-project).
  createMulti: async (
    payload: Omit<UserCreateRequest, 'appId'>,
    appIds: string[],
  ): Promise<{ created: User[]; failed: { appId: string; reason: string }[] }> => {
    const response = await api.post<ApiResponse<{ created: User[]; failed: { appId: string; reason: string }[] }>>(
      '/api/v1/users/create-multi',
      { ...payload, appIds },
    );
    return response.data.data;
  },

  // Update a user
  update: async (id: string, payload: UserUpdateRequest): Promise<User> => {
    const response = await api.put<ApiResponse<User>>(`/api/v1/users/update/${id}`, payload);
    return response.data.data;
  },

  // Preview what a delete will cascade-archive (assignments, devices, sessions)
  getDependencies: async (id: string): Promise<UserDependencies> => {
    const response = await api.get<ApiResponse<UserDependencies>>(`/api/v1/users/dependencies/${id}`);
    return response.data.data;
  },

  // Delete a user (soft delete; cascade-archives assignments, devices, sessions)
  delete: async (id: string): Promise<DeleteResponse> => {
    const response = await api.delete<ApiResponse<DeleteResponse>>(`/api/v1/users/delete/${id}`);
    return response.data.data;
  },

  // Restore a deleted user
  restore: async (id: string): Promise<User> => {
    const response = await api.patch<ApiResponse<User>>(`/api/v1/users/restore/${id}`);
    return response.data.data;
  },
};
